import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { Trans, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  ApiJobDetail,
  ApiJobDetailOk,
  ApiJobListCards,
  fetcher,
  paginatedServerSideQuery,
  PaginatedServerSideQueryResponse,
  buildSearchParams,
  isJobListCardsOk,
  isJobDetailOk,
  localeHasChanged,
} from '@utils/data-fetching';
import { useSwrSimilarJobsInfinite } from '@hooks/useSwrApi/endpoints/job';
import { LinkStyled } from '@components/common';
import {
  useFiltersQueryParams,
} from '@components/features/filters';
import { getI18nLinkHref } from '@services/i18nPaths';
import { JobCardList } from '@components/templates/job/Card/JobCardList';
import { JobListType } from '@type/job-params';
import { gsspGetLocale } from '@utils/gssp';
import { SIMILAR_JOB_LIST_ITEMS_PER_PAGE } from 'src/constants';

// Interface
// ******************************************
interface SimilarJobsPageProps {
  jobSlug: string,
  locale: Locale;
  similarJobsFromServer: ApiSchema<'JobResult'> | null;
  jobDetail: ApiJobDetailOk | null;
  infiniteQueryParams: PaginatedServerSideQueryResponse,
}

// Previous locale. Used to check if the locale has changed in getServerSideProps.
// ******************************************
let previousLocale: Locale | '' = '';

/**
 * @description Similar jobs page
 * - **jobSlug**: string - The job slug.
 * - **locale**: Locale - The current locale.
 * - **similarJobsFromServer**: ApiSchema<'JobResult'> | null - Similar jobs list, fetched from the server. Passed to SWR Infinite API.
 * - **jobDetail**: ApiJobDetailOk | null - Job detail. Used to render job title.
 * - **infiniteQueryParams**: PaginatedServerSideQueryResponse - The object that contains the query params for the infinite scrolling.
 */
const SimilarJobsPage = (props: SimilarJobsPageProps) => {
  // Destructure props
  const {
    jobSlug,
    locale,
    similarJobsFromServer,
    jobDetail,
    infiniteQueryParams,
  } = props;

  // Destructure paginatedQuery
  const {
    page,
    offset,
    limit,
    objFilterParams,
  } = infiniteQueryParams;

  // Translation
  const { t } = useTranslation('common');

  // Fetch data
  const {
    data,
    size,
    setSize,
    isLoading,
    isEmpty,
    sizeAsRef,
  } = useSwrSimilarJobsInfinite({
    jobSlug,
    locale,
    limit,
    serverSideData: similarJobsFromServer ?? undefined,
    startIndex: offset,
    // Pass the query params to the hook
    restUrlParams: buildSearchParams(objFilterParams, '&', false),
  });

  const totalPages = data?.at(-1)?.pages || 1;
  const lastLoadedPage = data?.at(-1)?.page || 1;

  // Helpers
  // ***********************************************
  const totalJobs = data?.at(-1)?.total || 0;
  const totalText = t('job-list.title.number', { total: totalJobs });

  // Handle query params in browser URL, history and filtering
  // ***********************************************
  const {
    setCurrentPage,
  } = useFiltersQueryParams({
    serverQueryParams: objFilterParams,
    initialPage: page,
  });

  // Render page component
  // *****************************************************
  return (
    <>
      {/* HEAD - title, meta, etc */}
      <Head>
        <title>{t('job.list.react.similar.to', { jobTitle: jobDetail?.title }).replace(/<\/?anchor>/g, '')}</title>
      </Head>

      {/* PAGE CONTENT */}
      <div className="min-h-screen bg-surface-100 md:min-h-3/4-screen">

        {/* Main content */}
        <div className="container">

          <div className="my-5">
            <div className="text-sm text-ink-medium">{totalText}</div>
            <div className="mt-2 text-sm">
              <Trans
                i18nKey="job.list.react.similar.to"
                values={{ jobTitle: jobDetail?.title }}
                components={{
                  anchor: <LinkStyled
                    styling="text"
                    href={`${getI18nLinkHref(`/job/${jobDetail?.slug}`, locale)}`}
                    className="!text-sm font-semibold"
                  />,
                }}
              />
            </div>
          </div>

          {/* Infinite list */}
          <JobCardList
            data={data}
            trackingData={{
              listType: 'similar',
            }}
            t={t}
            locale={locale}
            setCurrentPage={setCurrentPage}
            isLoading={isLoading}
            size={size}
            setSize={setSize}
            sizeAsRef={sizeAsRef}
            showLoadMore={lastLoadedPage < totalPages}
            isEmpty={isEmpty}
            listType={JobListType.SIMILAR_LIST}
          />

        </div>
      </div>
    </>
  );
};

// GSSP API
// query and params are needed for the initial request used by the infinite scrolling
// ************************************************************************************
export const getServerSideProps: GetServerSideProps<SimilarJobsPageProps> = async (context) => {
  const {
    req,
    query,
  } = context;

  const locale = gsspGetLocale(query);

  // Get the job slug from the query params
  const { jobSlug } = query;

  if (typeof jobSlug !== 'string') {
    return {
      notFound: true,
    };
  }

  // Reset the page to 1 if the locale changes. Treat it like a new search, otherwise the infinite scrolling
  // will continue from the last page of the previous locale and this might confuse the user.
  const localeChanged = localeHasChanged(locale, previousLocale);
  previousLocale = locale;
  const { pageNr, ...restQuery } = query;

  // Process the query for the infinite scrolling.
  const infiniteQueryParams = paginatedServerSideQuery({
    query: localeChanged ? restQuery : query,
    pageSlug: 'pageNr',
    itemsPerPage: SIMILAR_JOB_LIST_ITEMS_PER_PAGE,
    removeQueryParams: ['jobSlug', 'locale'],
  });

  // Fetch Data
  // *********************************
  const [similarJobsFromServer, jobDetailFromServer] = await Promise.all([
    fetcher<ApiJobListCards>(`/v3/jobs/similar${infiniteQueryParams.fetchUrlParams}&jobSlug=${jobSlug}`, locale, { ssrClientHeaders: req.headers }),
    fetcher<ApiJobDetail>(`/v1/job-operation/${jobSlug}/detail`, locale, { ssrClientHeaders: req.headers }),
  ]);

  // If job detail is not ok, then return a `notFound` error
  // *********************************
  if (!isJobDetailOk(jobDetailFromServer)) {
    return {
      notFound: true,
    };
  }

  // Return props
  // *********************************
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      locale,
      jobSlug,
      similarJobsFromServer: isJobListCardsOk(similarJobsFromServer) ? similarJobsFromServer : null,
      jobDetail: jobDetailFromServer,
      infiniteQueryParams,
    },
  };
};

// Page export
// ************************************************************************************
export default SimilarJobsPage;
